/**
 * iter-263 — Marketplace preview player (singleton).
 *
 * One <audio> element shared by every StemPackCard. Only one preview can
 * play at a time — clicking Play on a new card stops the previous one,
 * so the marketplace never turns into a cacophony.
 *
 * Audio source: each pack stores stem URLs on fal.media (CORS-strict).
 * We route every request through the iter-249 `/api/stems/proxy?url=…`
 * endpoint which adds `Access-Control-Allow-Origin: *` AND caches on
 * disk so repeated previews of the same pack are instant.
 *
 * Stem selection: prefer the most recognisable stem (vocals → other →
 * drums → first) so a one-shot preview communicates the pack's vibe.
 *
 * State shape (subscribers receive a fresh snapshot on every change):
 *   { packId, stemName, status, progress, error }
 *   status: 'idle' | 'loading' | 'playing' | 'error'
 *   progress: 0..1
 *
 * The module is intentionally framework-agnostic — tests can drive it
 * without rendering React.
 */

const PREVIEW_STEM_PREFERENCE = [
  "vocals", "vocal", "lead", "melody",
  "other", "mix", "instruments",
  "drums", "drum", "percussion",
  "bass", "guitar", "piano", "keys",
];

/**
 * Pick the most representative stem for preview from a pack's stem list.
 * Falls back to the first stem if no preferred name matches.
 */
export function pickPreviewStem(stems) {
  if (!Array.isArray(stems) || stems.length === 0) return null;
  const lower = stems.map((s) => ({ ...s, _lc: (s.name || "").toLowerCase() }));
  for (const pref of PREVIEW_STEM_PREFERENCE) {
    const hit = lower.find((s) => s._lc === pref || s._lc.includes(pref));
    if (hit) return { name: hit.name, url: hit.url };
  }
  return { name: stems[0].name, url: stems[0].url };
}

function _proxyUrl(rawUrl) {
  const api = process.env.REACT_APP_BACKEND_URL || "";
  // Our own backend URLs (e.g. /api/recording/file/…) are CORS-safe already.
  if (rawUrl.startsWith("/api/") || (api && rawUrl.startsWith(`${api}/api/`))) return rawUrl;
  return `${api}/api/stems/proxy?url=${encodeURIComponent(rawUrl)}`;
}

class MarketplacePreviewPlayer {
  constructor() {
    this._audio = null;
    this._state = { packId: null, stemName: null, status: "idle", progress: 0, error: null };
    this._subs = new Set();
    this._lastSrc = null;
  }

  _ensureAudio() {
    if (this._audio) return this._audio;
    // jsdom defines HTMLAudioElement; some legacy test envs don't.
    if (typeof Audio === "undefined") return null;
    const audio = new Audio();
    audio.preload = "auto";
    audio.crossOrigin = "anonymous";
    audio.addEventListener("timeupdate", () => {
      if (!this._audio || !this._state.packId) return;
      const dur = audio.duration;
      const cur = audio.currentTime;
      if (!Number.isFinite(dur) || dur <= 0) return;
      const next = Math.max(0, Math.min(1, cur / dur));
      // Throttle: only emit if progress moved by ≥0.5%.
      if (Math.abs(next - this._state.progress) >= 0.005) {
        this._emit({ progress: next });
      }
    });
    audio.addEventListener("ended", () => {
      this._emit({ status: "idle", progress: 0, packId: null, stemName: null });
    });
    audio.addEventListener("error", () => {
      // Browser couldn't load the source — surface a friendly state so the
      // card can render an "audio unavailable" affordance instead of a
      // perpetual spinner.
      this._emit({ status: "error", progress: 0, error: "Could not load preview" });
    });
    audio.addEventListener("playing", () => {
      this._emit({ status: "playing" });
    });
    this._audio = audio;
    return audio;
  }

  _emit(partial) {
    this._state = { ...this._state, ...partial };
    for (const fn of this._subs) {
      try { fn(this._state); } catch (e) { /* subscriber bug — never break the player */ void e; }
    }
  }

  subscribe(fn) {
    this._subs.add(fn);
    // Replay current state so late subscribers don't see a flash of stale UI.
    try { fn(this._state); } catch (e) { void e; }
    return () => { this._subs.delete(fn); };
  }

  getState() {
    return this._state;
  }

  /**
   * Start playing the given pack's preview. If something is already
   * playing, it gets stopped first. If THIS pack is already playing,
   * this call is a no-op (use `toggle` for the play/pause behaviour).
   */
  async play(packId, stemName, stemUrl) {
    if (!packId || !stemUrl) return;
    const audio = this._ensureAudio();
    if (!audio) return;

    // Already playing this exact stem — let it ride.
    if (this._state.packId === packId && this._state.status === "playing") return;

    const proxied = _proxyUrl(stemUrl);
    // Optimistic state — UI flips to loading immediately.
    this._emit({ packId, stemName, status: "loading", progress: 0, error: null });

    try {
      if (this._lastSrc !== proxied) {
        audio.src = proxied;
        this._lastSrc = proxied;
      } else {
        // Same src — just rewind.
        try { audio.currentTime = 0; } catch (e) { void e; }
      }
      await audio.play();
      // The "playing" event fires after this resolves — but on some
      // browsers play() resolves before the event, so we set the status
      // here too as a safety net.
      this._emit({ status: "playing" });
    } catch (e) {
      // Autoplay rejection (user hasn't gestured yet in this tab) OR
      // network/load error. Either way, surface error state.
      this._emit({ status: "error", error: e?.message || "Playback failed" });
    }
  }

  /**
   * Pause + reset to idle. If `onlyIfPackId` is supplied, only stops
   * when the currently-playing pack matches.
   */
  stop(onlyIfPackId = null) {
    if (onlyIfPackId && this._state.packId !== onlyIfPackId) return;
    const audio = this._audio;
    if (audio) {
      try { audio.pause(); } catch (e) { void e; }
      try { audio.currentTime = 0; } catch (e) { void e; }
    }
    this._emit({ status: "idle", progress: 0, packId: null, stemName: null, error: null });
  }

  /**
   * Convenience: toggle the play/pause state for a given pack.
   */
  toggle(packId, stemName, stemUrl) {
    if (this._state.packId === packId &&
        (this._state.status === "playing" || this._state.status === "loading")) {
      this.stop();
    } else {
      // Stop any currently-playing OTHER pack first.
      this.stop();
      void this.play(packId, stemName, stemUrl);
    }
  }
}

// Module singleton.
const _instance = new MarketplacePreviewPlayer();
export default _instance;

// Test-only escape hatch — lets tests reset the singleton between cases.
export function _resetForTests() {
  _instance.stop();
  _instance._audio = null;
  _instance._lastSrc = null;
  _instance._subs.clear();
  _instance._state = { packId: null, stemName: null, status: "idle", progress: 0, error: null };
}
